import type { FruitKind, LevelConfig, ThemeConfig, ThemeKey } from '../types';

// Levels 1–20 are grouped (4 levels per theme). Levels 21–25 are one per theme.
const THEME_BY_GROUP: ThemeKey[] = ['meadow', 'forest', 'river', 'pasture', 'mountains'];
const SOLO_THEMES_AFTER_20: ThemeKey[] = ['snowy', 'desert', 'jungle', 'sky', 'house'];

const FRUIT_BY_THEME: Record<ThemeKey, FruitKind> = {
  meadow: 'apple',
  forest: 'berry',
  river: 'melon',
  pasture: 'pear',
  mountains: 'golden_apple',
  snowy: 'apple',
  desert: 'pear',
  jungle: 'berry',
  sky: 'golden_apple',
  house: 'banana',
};

export const THEMES: Record<ThemeKey, ThemeConfig> = {
  meadow: {
    skyTop: 0x87ceeb,
    skyBottom: 0xfceabb,
    hillColor: 0x6ab04c,
    hillShadow: 0x4f8d3a,
    groundTextureKey: 'ground_meadow',
    cloudColor: 0xffffff,
    cloudAlpha: 0.92,
    decoration: 'trees',
  },
  forest: {
    // Overcast / rainy: solid grey sky, no blue.
    skyTop: 0x6e7682,
    skyBottom: 0x9098a0,
    hillColor: 0x2f5d2a,
    hillShadow: 0x1f4220,
    groundTextureKey: 'ground_forest',
    cloudColor: 0x4d535c,
    cloudAlpha: 0.7,
    decoration: 'pines',
  },
  river: {
    // Ocean / underwater theme.
    skyTop: 0x1f5d8c,
    skyBottom: 0x4ea3c7,
    hillColor: 0x2a7888,
    hillShadow: 0x1a4d5a,
    groundTextureKey: 'ground_river',
    cloudColor: 0xc4e0e8,
    cloudAlpha: 0.45,
    decoration: 'reeds',
  },
  pasture: {
    skyTop: 0xf5c97a,
    skyBottom: 0xfff1c4,
    hillColor: 0x88a83a,
    hillShadow: 0x607a26,
    groundTextureKey: 'ground_pasture',
    cloudColor: 0xfff1c4,
    cloudAlpha: 0.9,
    decoration: 'rocks',
  },
  mountains: {
    skyTop: 0x4a5b8a,
    skyBottom: 0xb6c5e3,
    hillColor: 0x6a6f80,
    hillShadow: 0x40455a,
    groundTextureKey: 'ground_mountains',
    cloudColor: 0xe7ecf5,
    cloudAlpha: 0.95,
    decoration: 'snowcaps',
  },
  snowy: {
    skyTop: 0x88a2c2,
    skyBottom: 0xdfe8f2,
    hillColor: 0xeaf2fa,
    hillShadow: 0xb8c8d8,
    groundTextureKey: 'ground_snowy',
    cloudColor: 0xffffff,
    cloudAlpha: 0.95,
    decoration: 'snow',
  },
  desert: {
    skyTop: 0xeaa75a,
    skyBottom: 0xf6e1b8,
    hillColor: 0xc88a4a,
    hillShadow: 0x8a5a2a,
    groundTextureKey: 'ground_desert',
    cloudColor: 0xffe4b8,
    cloudAlpha: 0.85,
    decoration: 'cactus',
  },
  jungle: {
    skyTop: 0x4a8a3a,
    skyBottom: 0x9ec48a,
    hillColor: 0x2a6822,
    hillShadow: 0x1a4015,
    groundTextureKey: 'ground_jungle',
    cloudColor: 0xc8e4c0,
    cloudAlpha: 0.7,
    decoration: 'jungle',
  },
  sky: {
    skyTop: 0x9bc8e8,
    skyBottom: 0xeaf2fa,
    hillColor: 0xffffff,
    hillShadow: 0xc8d8e8,
    groundTextureKey: 'ground_sky',
    cloudColor: 0xffffff,
    cloudAlpha: 1,
    decoration: 'clouds',
  },
  house: {
    skyTop: 0xe4d4b8,
    skyBottom: 0xfaf3e1,
    hillColor: 0xa86a3a,
    hillShadow: 0x6b3410,
    groundTextureKey: 'ground_house',
    cloudColor: 0xfaf3e1,
    cloudAlpha: 0,
    decoration: 'toys',
  },
};

function difficultyFor(level: number) {
  const t = Math.min(1, (level - 1) / 24); // 0..1 across 1..25
  return {
    // Cap how fast later levels get so even level 25 stays playable.
    speedMultiplier: Math.min(1.4, 1 + 0.025 * (level - 1)),
    spawnMinGap: Math.round(900 - 480 * t),
    spawnMaxGap: Math.round(1400 - 600 * t),
    duckChance: 0.1 + 0.35 * t,
    fruitChance: 0.42 - 0.1 * t,
  };
}

function themeForLevel(level: number): ThemeKey {
  if (level <= 20) {
    const groupIdx = Math.min(4, Math.floor((level - 1) / 4));
    return THEME_BY_GROUP[groupIdx];
  }
  // Levels 21–25: one solo theme per level.
  return SOLO_THEMES_AFTER_20[level - 21] ?? SOLO_THEMES_AFTER_20[SOLO_THEMES_AFTER_20.length - 1];
}

function targetForLevel(level: number): number {
  return 5 + Math.floor((level - 1) / 2);
}

export const LEVELS: Record<number, LevelConfig> = (() => {
  const out: Record<number, LevelConfig> = {};
  for (let n = 1; n <= 25; n++) {
    const theme = themeForLevel(n);
    out[n] = {
      level: n,
      theme,
      fruit: FRUIT_BY_THEME[theme],
      target: targetForLevel(n),
      difficulty: difficultyFor(n),
      hasBossAtEnd: n % 5 === 0,
    };
  }
  return out;
})();

export const MAX_LEVEL = 25;
